import OpenAI from 'openai'
import Anthropic from '@anthropic-ai/sdk'
import { GoogleGenerativeAI } from '@google/generative-ai'
import { UploadedDocument } from '../store/DocumentContext'

export interface AIRequest {
  message: string
  documentContent?: string
  context?: string
  conversationHistory?: ConversationMessage[]
  uploadedDocuments?: UploadedDocument[]
  type: 'conversation' | 'document_generation' | 'refinement'
}

export interface AIResponse {
  content: string
  intent?: string
  confidence: number
  isComplete?: boolean
  documentContent?: string
  metadata?: {
    documentType?: string
    phase?: string
    readyToDraft?: boolean
    gatheredInfo?: Record<string, any>
  }
}

export interface ConversationMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

// Pure AI-driven service with no templates or canned responses
export class IntelligentAIService {
  private static instance: IntelligentAIService
  private apiKey: string | null = null
  private provider: 'openai' | 'anthropic' | 'gemini' = 'gemini'
  private openaiClient: OpenAI | null = null
  private anthropicClient: Anthropic | null = null
  private geminiClient: GoogleGenerativeAI | null = null
  private conversationHistory: ConversationMessage[] = []
  private uploadedDocuments: UploadedDocument[] = []
  // Configurable generation options
  private modelOverride: string | null = null
  private temperatureOverride: number | null = null
  private maxTokensConversation: number = 8000
  private maxTokensDocument: number = 32000

  static getInstance(): IntelligentAIService {
    if (!IntelligentAIService.instance) {
      IntelligentAIService.instance = new IntelligentAIService()
    }
    return IntelligentAIService.instance
  }

  setApiKey(key: string, provider: 'openai' | 'anthropic' | 'gemini' = 'gemini'): void {
    this.apiKey = key
    this.provider = provider
    
    if (provider === 'openai') {
      const baseUrl = import.meta.env.VITE_GROK_BASE_URL || process.env.GROK_BASE_URL || 'https://api.openai.com/v1'
      this.openaiClient = new OpenAI({ 
        apiKey: key,
        baseURL: baseUrl,
        dangerouslyAllowBrowser: true
      })
    } else if (provider === 'anthropic') {
      this.anthropicClient = new Anthropic({ 
        apiKey: key,
        dangerouslyAllowBrowser: true
      })
    } else if (provider === 'gemini') {
      this.geminiClient = new GoogleGenerativeAI(key)
    }
  }

  // Allow callers to override model, temperature, and token limits
  setOptions(options: {
    model?: string
    temperature?: number
    maxTokensConversation?: number
    maxTokensDocument?: number
  }): void {
    if (typeof options.model === 'string' && options.model.trim().length > 0) {
      this.modelOverride = options.model.trim()
    }
    if (typeof options.temperature === 'number' && !Number.isNaN(options.temperature)) {
      this.temperatureOverride = options.temperature
    }
    if (typeof options.maxTokensConversation === 'number' && options.maxTokensConversation > 0) {
      this.maxTokensConversation = options.maxTokensConversation
    }
    if (typeof options.maxTokensDocument === 'number' && options.maxTokensDocument > 0) {
      this.maxTokensDocument = options.maxTokensDocument
    }
  }

  setUploadedDocuments(documents: UploadedDocument[]): void {
    this.uploadedDocuments = documents
  }

  async processUserInput(userMessage: string, uploadedDocuments?: UploadedDocument[]): Promise<AIResponse> {
    // Update uploaded documents if provided
    if (uploadedDocuments) {
      this.setUploadedDocuments(uploadedDocuments)
    }

    // Add user message to conversation history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    })

    try {
      // Use AI to determine the response based on conversation context
      const aiResponse = await this.generateIntelligentResponse(userMessage)
      
      // Add AI response to conversation history
      this.conversationHistory.push({
        role: 'assistant',
        content: aiResponse.content,
        timestamp: new Date()
      })

      return aiResponse
    } catch (error) {
      console.error('Error processing user input:', error)
      return this.generateErrorResponse()
    }
  }

  private async generateIntelligentResponse(userMessage: string): Promise<AIResponse> {
    if (!this.isConfigured()) {
      return this.generateLocalFallback(userMessage)
    }

    try {
      const systemPrompt = this.buildSystemPrompt()
      const conversationContext = this.buildConversationContext()
      
      if (this.provider === 'openai' && this.openaiClient) {
        return await this.generateOpenAIResponse(systemPrompt, conversationContext, userMessage)
      } else if (this.provider === 'anthropic' && this.anthropicClient) {
        return await this.generateAnthropicResponse(systemPrompt, conversationContext, userMessage)
      } else if (this.provider === 'gemini' && this.geminiClient) {
        return await this.generateGeminiResponse(systemPrompt, conversationContext, userMessage)
      }
      
      return this.generateLocalFallback(userMessage)
    } catch (error) {
      console.error('AI API call failed:', error)
      return this.generateLocalFallback(userMessage)
    }
  }

  private buildSystemPrompt(): string {
    const uploadedDocsContext = this.uploadedDocuments.length > 0 
      ? `\n\nUPLOADED REFERENCE DOCUMENTS:
The user has provided the following documents for context. Use these documents to inform your responses and recommendations:

${this.uploadedDocuments.map(doc => `
Document: ${doc.name} (${doc.type})
Content: ${doc.content.substring(0, 2000)}${doc.content.length > 2000 ? '...' : ''}
`).join('\n')}

When creating new documents, consider these reference documents and incorporate relevant information, terms, or structures where appropriate.`
      : ''

    return `You are an expert lawyer with deep knowledge of legal document drafting, research, and analysis. Your role is to:

1. **Engage in natural conversation** - Respond naturally and conversationally, just like a knowledgeable legal professional would.

2. **Ask intelligent follow-up questions** - Based on what the user has told you, ask relevant questions that help you understand their needs better. These should be natural questions that flow from the conversation. ALWAYS ask multiple clarifying questions before considering document generation.

3. **Understand context deeply** - Pay attention to what the user has already told you and build on that information. Remember details from the conversation.

4. **Gather comprehensive information FIRST** - You must collect detailed information through conversation before offering to draft anything. This includes names, addresses, specific terms, obligations, timelines, and jurisdiction. A single initial request is NEVER enough information.

5. **Know when you have enough information** - Only after thorough information gathering through multiple exchanges should you offer to draft the document.

6. **Generate documents from scratch** - NEVER use templates or placeholder text. Write complete, professional legal documents naturally based on the information gathered.

7. **Be conversational and helpful** - Act like a knowledgeable legal assistant who genuinely wants to help.

8. **Use reference documents** - If the user has uploaded documents for context, reference them appropriately in your responses and incorporate relevant information when creating new documents.

IMPORTANT RULES:
- NEVER use template language like "Party 1" or "[INSERT NAME]" or placeholder brackets
- NEVER ask predetermined lists of questions
- Always respond naturally based on the conversation flow
- When drafting documents, write them completely from scratch using the specific information provided
- Ask follow-up questions that make sense given what you already know
- Don't be robotic - be conversational and natural
- If someone mentions a specific document type (like "prenup", "employment contract", etc.), ask relevant questions about their specific situation
- CRITICAL: Never offer to generate a document on the first interaction - always gather information through multiple conversation exchanges first
- Build on previous conversation context naturally
- When someone asks for a specific document (like "prenup"), ask about their specific circumstances, assets, children, etc.
- Be empathetic and understanding - legal matters can be sensitive
- Guide the conversation naturally toward gathering the information needed for the document
- Reference uploaded documents when relevant to provide better, more informed assistance

DOCUMENT GENERATION:
You must gather comprehensive information through natural conversation before generating any document. Only include the text "[READY_TO_GENERATE]" when you have collected ALL relevant information including but not limited to the following minimum requirements:

FOR ANY LEGAL DOCUMENT:
- Full legal names of all parties
- Complete addresses and contact information
- Specific document purpose and scope
- Key terms, obligations, and responsibilities
- Payment/compensation details (if applicable)
- Timeline and important dates
- Governing jurisdiction and applicable law

NEVER generate a document until you have asked follow-up questions and gathered detailed, specific information. A simple initial request is NEVER sufficient. You must engage in conversation to understand the complete situation before offering to draft anything.

The document should be extremely detailed and comprehensive. It should be a complete document that could be printed and signed. Please make sure to be thorough and cover all the possible corner cases that a law partner would cover.

CRITICAL REQUIREMENTS FOR COMPLETE LEGAL DOCUMENTS:
- Include ALL relevant schedules, exhibits, and attachments that would typically accompany the document type
- Add comprehensive appendices with detailed terms, definitions, and provisions
- Include standard legal schedules such as: Schedule A (Property/Assets), Schedule B (Liabilities/Debts), Schedule C (Personal Property), etc. as appropriate for the document type
- Create detailed exhibits for complex terms, payment schedules, property descriptions, or technical specifications
- Ensure the document is complete with all supporting documentation that would be expected in a professional legal practice
- Add signature pages for all parties, witnesses, and notarization as legally required
- Include standard legal clauses like severability, governing law, dispute resolution, and force majeure where appropriate

${uploadedDocsContext}
`
  }

  private buildConversationContext(): string {
    const recent = this.conversationHistory.slice(-10) // Get last 10 messages
    return recent.map(msg => `${msg.role}: ${msg.content}`).join('\n\n')
  }

  private async generateOpenAIResponse(systemPrompt: string, context: string, userMessage: string): Promise<AIResponse> {
    const fallbackModel = import.meta.env.VITE_GROK_MODEL || process.env.GROK_MODEL || 'gpt-4-turbo-preview'
    const model = this.modelOverride || fallbackModel
    const temperature = this.temperatureOverride ?? 0.7
    
    const messages: any[] = [
      { role: 'system', content: systemPrompt }
    ]

    // Add conversation history
    if (context) {
      messages.push({ role: 'user', content: `Previous conversation:\n${context}` })
    }

    // Add current user message
    messages.push({ role: 'user', content: userMessage })

    const completion = await this.openaiClient!.chat.completions.create({
      model: model,
      messages: messages,
      temperature: temperature,
      max_tokens: this.maxTokensConversation
    })

    const responseContent = completion.choices[0]?.message?.content || 'I apologize, but I had trouble generating a response.'

    // Analyze the response to determine if we should generate a document
    const shouldGenerateDocument = await this.shouldGenerateDocument(responseContent, userMessage)

    if (shouldGenerateDocument) {
      const documentContent = await this.generateDocumentWithAI()
      // Clean up the response content by removing the marker
      const cleanResponseContent = responseContent.replace("[READY_TO_GENERATE]", '').trim()
      return {
        content: cleanResponseContent,
        intent: 'document_generation',
        confidence: 0.9,
        isComplete: true,
        documentContent: documentContent,
        metadata: {
          phase: 'drafting',
          readyToDraft: true
        }
      }
    }

    return {
      content: responseContent,
      intent: 'conversation',
      confidence: 0.8,
      isComplete: false
    }
  }

  private async generateAnthropicResponse(systemPrompt: string, context: string, userMessage: string): Promise<AIResponse> {
    const messages: any[] = []

    if (context) {
      messages.push({ role: 'user', content: `Previous conversation:\n${context}` })
      messages.push({ role: 'assistant', content: 'I understand the previous conversation context.' })
    }

    messages.push({ role: 'user', content: userMessage })

    const completion = await this.anthropicClient!.messages.create({
      model: 'claude-3-sonnet-20240229',
      max_tokens: this.maxTokensConversation,
      temperature: this.temperatureOverride ?? 0.7,
      system: systemPrompt,
      messages: messages
    })

    const responseContent = completion.content[0]?.type === 'text' ? completion.content[0].text : 'I apologize, but I had trouble generating a response.'

    const shouldGenerateDocument = await this.shouldGenerateDocument(responseContent, userMessage)

    if (shouldGenerateDocument) {
      const documentContent = await this.generateDocumentWithAI()
      // Clean up the response content by removing the marker
      const cleanResponseContent = responseContent.replace("[READY_TO_GENERATE]", '').trim()
      return {
        content: cleanResponseContent,
        intent: 'document_generation',
        confidence: 0.9,
        isComplete: true,
        documentContent: documentContent,
        metadata: {
          phase: 'drafting',
          readyToDraft: true
        }
      }
    }

    return {
      content: responseContent,
      intent: 'conversation',
      confidence: 0.8,
      isComplete: false
    }
  }

  private async generateGeminiResponse(systemPrompt: string, context: string, userMessage: string): Promise<AIResponse> {
    const model = this.geminiClient!.getGenerativeModel({ 
      model: 'gemini-2.0-flash-exp',
      generationConfig: {
        temperature: this.temperatureOverride ?? 0.7,
        maxOutputTokens: this.maxTokensConversation
      }
    })

    // Build the conversation history for Gemini
    const parts = []
    
    // Add system prompt
    parts.push({ text: systemPrompt })
    
    // Add conversation history if available
    if (context) {
      parts.push({ text: `Previous conversation:\n${context}` })
    }
    
    // Add current user message
    parts.push({ text: userMessage })

    const result = await model.generateContent({
      contents: [{ role: 'user', parts }]
    })

    const responseContent = result.response.text() || 'I apologize, but I had trouble generating a response.'

    // Check if we should generate a document
    const shouldGenerateDocument = await this.shouldGenerateDocument(responseContent, userMessage)

    if (shouldGenerateDocument) {
      const documentContent = await this.generateDocumentWithAI()
      // Clean up the response content by removing the marker
      const cleanResponseContent = responseContent.replace("[READY_TO_GENERATE]", '').trim()
      return {
        content: cleanResponseContent,
        intent: 'document_generation',
        confidence: 0.9,
        isComplete: true,
        documentContent: documentContent,
        metadata: {
          phase: 'drafting',
          readyToDraft: true
        }
      }
    }

    return {
      content: responseContent,
      intent: 'conversation',
      confidence: 0.8,
      isComplete: false
    }
  }

  private async shouldGenerateDocument(aiResponse: string, userMessage: string): Promise<boolean> {
    // Check if the AI has indicated it's ready to generate the document
    const hasReadyMarker = aiResponse.indexOf("[READY_TO_GENERATE]") !== -1
    
    if (!hasReadyMarker) {
      return false
    }
    
    // Additional safeguards: ensure we have enough conversation history
    // Don't generate on the first exchange - need at least some back-and-forth
    if (this.conversationHistory.length < 4) { // At least 2 user messages and 2 AI responses
      console.log('Not enough conversation history for document generation')
      return false
    }
    
    // Check that we have substantial conversation content
    const conversationText = this.conversationHistory.map(msg => msg.content).join(' ')
    
    // Require minimum conversation length (rough indicator of information gathering)
    if (conversationText.length < 500) {
      console.log('Conversation too short for document generation')
      return false
    }
    
    // Look for signs that information has been gathered (questions asked/answered)
    const hasQuestionWords = /\b(who|what|when|where|why|how|which|whose)\b/gi.test(conversationText)
    const hasSpecificDetails = /\b(name|address|date|amount|term|condition|payment|jurisdiction)\b/gi.test(conversationText)
    
    if (!hasQuestionWords || !hasSpecificDetails) {
      console.log('Insufficient detail gathering detected in conversation')
      return false
    }
    
    return true
  }

  private async generateDocumentWithAI(): Promise<string> {
    if (!this.isConfigured()) {
      return "I apologize, but I need an API key configured to generate documents. Please configure your AI service."
    }

    try {
      const documentPrompt = this.buildDocumentGenerationPrompt()
      
      if (this.provider === 'openai' && this.openaiClient) {
        const fallbackModel = import.meta.env.VITE_GROK_MODEL || process.env.GROK_MODEL || 'gpt-4-turbo-preview'
        const model = this.modelOverride || fallbackModel
        
        const completion = await this.openaiClient.chat.completions.create({
          model: model,
          messages: [
            {
              role: 'system',
              content: `You are an expert legal document drafter. Create complete, professional legal documents from scratch using proper HTML formatting for legal presentation. 

CRITICAL FORMATTING REQUIREMENTS:
- Start with an <h1> tag for the document title (e.g., "EMPLOYMENT AGREEMENT")
- Use <h2> tags for major sections with numbers (e.g., "1. PARTIES", "2. POSITION AND DUTIES")
- Use <h3> tags for subsections if needed
- Use <p> tags for all paragraph content with proper legal language
- Use <ol> or <ul> tags for numbered/bulleted lists of terms and conditions
- Use <strong> tags to emphasize important terms, definitions, and key provisions
- Include proper signature sections at the end with signature lines
- Use professional legal formatting and language throughout
- Never use templates or placeholders like "[Name]" or "Party 1"

COMPREHENSIVE LEGAL DOCUMENT REQUIREMENTS:
- Include ALL relevant schedules, exhibits, and attachments that would typically accompany the document type
- Add detailed schedules such as: Schedule A (Property/Assets), Schedule B (Liabilities/Debts), Schedule C (Personal Property), Schedule D (Payment Terms), etc.
- Create comprehensive exhibits for complex terms, detailed descriptions, technical specifications, or supporting information
- Include all standard legal provisions: severability, governing law, dispute resolution, force majeure, amendment procedures, etc.
- Add proper signature blocks for all parties, witnesses, and notarization requirements
- Ensure the document is complete with all supporting documentation expected in professional legal practice
- Make the document thorough enough that it could be filed with courts or government agencies without additional documentation

The document must look professional and legally viable, formatted like a real legal document that could be printed and signed immediately.`
            },
            {
              role: 'user',
              content: documentPrompt
            }
          ],
          temperature: this.temperatureOverride ?? 0.3,
          max_tokens: this.maxTokensDocument
        })

        return completion.choices[0]?.message?.content || 'Unable to generate document.'
      } else if (this.provider === 'anthropic' && this.anthropicClient) {
        const completion = await this.anthropicClient.messages.create({
          model: 'claude-3-sonnet-20240229',
          max_tokens: this.maxTokensDocument,
          temperature: this.temperatureOverride ?? 0.3,
          system: 'You are an expert legal document drafter. Create complete, professional legal documents from scratch based on the conversation context. Never use templates or placeholders.',
          messages: [{ role: 'user', content: documentPrompt }]
        })

        return completion.content[0]?.type === 'text' ? completion.content[0].text : 'Unable to generate document.'
      } else if (this.provider === 'gemini' && this.geminiClient) {
        const model = this.geminiClient.getGenerativeModel({ 
          model: 'gemini-2.0-flash-exp',
          generationConfig: {
            temperature: this.temperatureOverride ?? 0.3,
            maxOutputTokens: this.maxTokensDocument
          }
        })

        const systemPrompt = `You are an expert legal document drafter. Create complete, professional legal documents from scratch using proper HTML formatting for legal presentation. 

CRITICAL FORMATTING REQUIREMENTS:
- Start with an <h1> tag for the document title (e.g., "EMPLOYMENT AGREEMENT")
- Use <h2> tags for major sections with numbers (e.g., "1. PARTIES", "2. POSITION AND DUTIES")
- Use <h3> tags for subsections if needed
- Use <p> tags for all paragraph content with proper legal language
- Use <ol> or <ul> tags for numbered/bulleted lists of terms and conditions
- Use <strong> tags to emphasize important terms, definitions, and key provisions
- Include proper signature sections at the end with signature lines
- Use professional legal formatting and language throughout
- Never use templates or placeholders like "[Name]" or "Party 1"

COMPREHENSIVE LEGAL DOCUMENT REQUIREMENTS:
- Include ALL relevant schedules, exhibits, and attachments that would typically accompany the document type
- Add detailed schedules such as: Schedule A (Property/Assets), Schedule B (Liabilities/Debts), Schedule C (Personal Property), Schedule D (Payment Terms), etc.
- Create comprehensive exhibits for complex terms, detailed descriptions, technical specifications, or supporting information
- Include all standard legal provisions: severability, governing law, dispute resolution, force majeure, amendment procedures, etc.
- Add proper signature blocks for all parties, witnesses, and notarization requirements
- Ensure the document is complete with all supporting documentation expected in professional legal practice
- Make the document thorough enough that it could be filed with courts or government agencies without additional documentation

The document must look professional and legally viable, formatted like a real legal document that could be printed and signed immediately.`

        const result = await model.generateContent({
          contents: [
            { role: 'user', parts: [{ text: systemPrompt }, { text: documentPrompt }] }
          ]
        })

        return result.response.text() || 'Unable to generate document.'
      }

      return "Unable to generate document - API not configured properly."
    } catch (error) {
      console.error('Document generation failed:', error)
      return "I encountered an error while generating the document. Please try again."
    }
  }

  private detectDocumentType(): string {
    // Analyze conversation history to determine document type
    const conversationText = this.conversationHistory.map(msg => msg.content).join(' ').toLowerCase()
    
    if (conversationText.indexOf('prenup') !== -1 || conversationText.indexOf('prenuptial') !== -1) {
      return 'Prenuptial Agreement'
    }
    if (conversationText.indexOf('employment') !== -1 || conversationText.indexOf('job') !== -1) {
      return 'Employment Agreement'
    }
    if (conversationText.indexOf('contract') !== -1) {
      return 'Contract'
    }
    if (conversationText.indexOf('agreement') !== -1) {
      return 'Agreement'
    }
    
    return 'Legal Document'
  }

  private buildDocumentGenerationPrompt(): string {
    const conversationSummary = this.conversationHistory.map(msg => `${msg.role}: ${msg.content}`).join('\n\n')
    
    const uploadedDocsContext = this.uploadedDocuments.length > 0 
      ? `\n\nREFERENCE DOCUMENTS PROVIDED BY USER:
${this.uploadedDocuments.map(doc => `
Document: ${doc.name} (${doc.type})
Content: ${doc.content}
`).join('\n')}

Use these reference documents to inform the structure, language, and terms of the new document. Incorporate relevant clauses, terminology, and legal concepts from these documents where appropriate.`
      : ''
    
    return `Based on this entire conversation, please create a complete, professional legal document from scratch using proper HTML formatting for professional presentation.

Conversation history:
${conversationSummary}${uploadedDocsContext}

IMPORTANT INSTRUCTIONS:
1. Write the document completely from scratch - do NOT use any templates or placeholder text
2. Use the specific names, details, and information provided in the conversation
3. Create a professionally formatted legal document with proper HTML structure
4. Include all relevant legal provisions and clauses that would be expected in this type of document
5. Use proper legal language and structure
6. Make sure the document is complete and ready to use
7. Use the actual names and details mentioned in the conversation - do not use generic placeholders
8. If reference documents are provided, incorporate relevant terms, structures, and legal concepts from them

COMPREHENSIVE DOCUMENT REQUIREMENTS:
- Include ALL schedules, exhibits, and attachments relevant to the document type
- Add detailed schedules: Schedule A (Assets/Property), Schedule B (Liabilities), Schedule C (Personal Property), Schedule D (Payment Terms), etc.
- Create exhibits for complex terms, detailed descriptions, payment schedules, property descriptions, technical specifications
- Include standard legal clauses: severability, governing law, dispute resolution, force majeure, amendment procedures, entire agreement, etc.
- Add comprehensive signature blocks for all parties, witnesses, and notarization as legally required
- Make the document complete enough to file with courts or agencies without additional documentation
- Ensure all supporting documentation that would be expected in professional legal practice is included

FORMATTING REQUIREMENTS:
- Use HTML formatting for professional presentation
- Include a clear document title with <h1> tags
- Use <h2> tags for major sections (e.g., "1. PARTIES", "2. POSITION AND DUTIES", etc.)
- Use <h3> tags for subsections if needed
- Use <p> tags for paragraphs with proper spacing
- Use numbered or lettered lists (<ol>, <ul>) for clauses and sub-clauses
- Use <strong> tags for important terms and definitions
- Use proper line spacing and indentation
- Include signature lines and date fields at the end
- Make it look like a professional legal document that could be printed and signed

Generate the complete, properly formatted HTML document now:`
  }

  private generateLocalFallback(userMessage: string): AIResponse {
    // Remove all keyword extraction and canned responses
    // Instead, provide a simple fallback that encourages natural conversation
    
    return {
      content: "I'm here to help you with legal documents and contracts. Please tell me what you need in your own words, and I'll guide you through the process with relevant questions.",
      intent: 'conversation',
      confidence: 0.5,
      isComplete: false
    }
  }

  private generateErrorResponse(): AIResponse {
    return {
      content: "I encountered an error while processing your request. Please try again, and if the problem persists, you may need to check your AI service configuration.",
      intent: 'error',
      confidence: 0,
      isComplete: false
    }
  }

  // Reset conversation
  resetConversation(): void {
    this.conversationHistory = []
  }

  // Get conversation history
  getConversationHistory(): ConversationMessage[] {
    return [...this.conversationHistory]
  }

  // Get current context for debugging
  getCurrentContext(): any {
    return {
      conversationLength: this.conversationHistory.length,
      isConfigured: this.isConfigured()
    }
  }

  // Check if AI service is configured
  isConfigured(): boolean {
    return !!this.apiKey && (!!this.openaiClient || !!this.anthropicClient || !!this.geminiClient)
  }
}

export default IntelligentAIService.getInstance()
